import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  Box,
  TextField,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faBolt,
  faTrophy,
  faEarthAmericas,
  faBuilding,
  faDownload,
  faCircleInfo,
  faFilter,
  faUndo,
  faPen,
  faProjectDiagram,
  faFileExport,
  faPlug,
  faRecycle,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useDonorGHGData } from "../../hooks/useDonorGHGData";
import { usePermissionGuard } from "../../hooks/usePermissionGuard";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import Alert from "../../components/ui/Alert";
import LastUpdatedBadge from "../../components/ui/LastUpdatedBadge";
import ActionButton from "../../components/ui/ActionButton";
import Skeleton from "../../components/ui/Skeleton";
import SankeyPanel from "../../components/DonorGHG/SankeyPanel";
import CreateReportModal from "../../components/Donor/CreateReportModal";

import "../../style/DonorGHG.css";

const formatNum = (n) => n?.toLocaleString() ?? "0";

// format "last saved" timestamp for goals
const formatLastSaved = (timestamp) => {
  if (!timestamp) return null;

  const date = new Date(timestamp);

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

// preset range calculator
const getPresetRange = (preset) => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3);

  switch (preset) {
    case "thisMonth":
      return {
        start: new Date(year, month, 1),
        end: new Date(year, month + 1, 0),
        label: `${new Date(year, month, 1).toLocaleString("default", { month: "long" })} ${year}`,
      };
    case "lastMonth": {
      const lmYear = month === 0 ? year - 1 : year;
      const lmMonth = month === 0 ? 11 : month - 1;
      return {
        start: new Date(lmYear, lmMonth, 1),
        end: new Date(lmYear, lmMonth + 1, 0),
        label: `${new Date(lmYear, lmMonth, 1).toLocaleString("default", { month: "long" })} ${lmYear}`,
      };
    }
    case "thisQuarter":
      return {
        start: new Date(year, quarter * 3, 1),
        end: new Date(year, quarter * 3 + 3, 0),
        label: `Q${quarter + 1} ${year}`,
      };
    case "thisYear":
      return {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31),
        label: `${year}`,
      };
    case "lastYear":
      return {
        start: new Date(year - 1, 0, 1),
        end: new Date(year - 1, 11, 31),
        label: `${year - 1}`,
      };
    case "allTime":
      return { start: null, end: null, label: "All Time" };
    default:
      return { start: null, end: null, label: "Custom Range" };
  }
};

const toInputDate = (d) => (d ? d.toISOString().split("T")[0] : "");

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// get period label for hero section
const getPeriodLabel = (preset, dateFrom, dateTo) => {
  if (preset && preset !== "custom") {
    const range = getPresetRange(preset);
    return range.label;
  }
  if (dateFrom && dateTo) {
    const fromYear = new Date(dateFrom).getFullYear();
    const toYear = new Date(dateTo).getFullYear();
    if (fromYear === toYear) return `${fromYear}`;
    return `${fromYear}–${toYear}`;
  }
  return "All Time";
};

// format the 30-day trend - show percentage only when meaningful
const formatTrendValue = (rolling30) => {
  if (!rolling30) return { value: "—", hasBaseline: false };

  const current = rolling30.current || 0;
  const previous = rolling30.previous || 0;
  const pctChange = rolling30.percentChange || 0;
  const deliveryCount = rolling30.deliveryCount || 0;

  // If no current data
  if (current === 0) {
    return {
      value: "0 kg",
      hasBaseline: false,
      isFirstPeriod: true,
    };
  }

  // If previous period had too little data (< 10% of current, or < 50 kg),
  // show the actual current value instead of a misleading percentage
  const previousTooSmall =
    previous < 50 || (previous > 0 && previous < current * 0.1);

  if (previous === 0 || previousTooSmall) {
    return {
      value: `${formatNum(Math.round(current))} kg`,
      hasBaseline: false,
      isFirstPeriod: true,
      deliveryCount,
    };
  }

  // Both periods have meaningful data - show percentage
  // Cap at + or - 200% for reasonable display
  const displayPct = Math.min(200, Math.max(-200, Math.round(pctChange)));
  const cappedDisplay =
    Math.abs(pctChange) > 200
      ? `${displayPct > 0 ? ">" : "<"}${displayPct}%`
      : `${displayPct >= 0 ? "+" : ""}${displayPct}%`;

  return {
    value: cappedDisplay,
    hasBaseline: true,
    trend: pctChange,
    deliveryCount,
  };
};

const ImpactHero = ({
  total,
  goalTarget,
  goalPct,
  periodLabel,
  darkMode,
  isLoading,
  goalsUpdatedAt,
  onConfigureClick,
  onVisualizeClick,
  isVisualizingFlow,
}) => {
  const { t } = useTranslation();
  const radius = 54;
  const circ = 2 * Math.PI * radius;
  const hasGoalSet = !!goalTarget; // check if there's a goal for the selected year
  const offset = hasGoalSet ? circ * (1 - goalPct / 100) : circ;

  return (
    <div className={`ghg-impact-hero ${darkMode ? "dark" : "light"}`}>
      <div className="ghg-impact-main">
        <div className="ghg-impact-label">
          <FontAwesomeIcon icon={faEarthAmericas} />
          <span>{periodLabel} {t("Environmental Impact")}</span>
        </div>
        <div className="ghg-impact-value">
          {isLoading ? (
            <Skeleton width="200px" height="56px" borderRadius="8px" />
          ) : (
            <>
              {formatNum(Math.round(total))}{" "}
              <span className="ghg-impact-unit">kg CO₂e</span>
            </>
          )}
        </div>
        <div className="ghg-impact-subtitle">
          {t("Total emissions avoided through your material donations")}
        </div>
        {!isLoading && (
          <button
            className={`ghg-visualize-btn ${isVisualizingFlow ? "active" : ""}`}
            onClick={onVisualizeClick}
          >
            <FontAwesomeIcon icon={faProjectDiagram} />
            <span>{isVisualizingFlow ? t("Hide Material Flow") : t("Explore Material Flow (Sankey Diagram)")}</span>
          </button>
        )}
      </div>

      {hasGoalSet ? (
        <div className="ghg-goal-section">
          <div className="ghg-impact-ring">
            <svg width="120" height="120" viewBox="0 0 120 120">
              <circle className="ghg-ring-bg" cx="60" cy="60" r={radius} />
              <circle
                className="ghg-ring-progress"
                cx="60"
                cy="60"
                r={radius}
                style={{
                  strokeDasharray: circ,
                  strokeDashoffset: isLoading ? circ : offset,
                }}
              />
            </svg>
            <div className="ghg-ring-text">
              {isLoading ? (
                <Skeleton width="50px" height="28px" borderRadius="4px" />
              ) : (
                <div className="ghg-ring-percent">{goalPct}%</div>
              )}
            </div>
          </div>
          <div className="ghg-goal-info">
            <span className="ghg-goal-label">Yearly Goal</span>
            <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              <span className="ghg-goal-value">{formatNum(goalTarget)} kg</span>
              <ActionButton
                variant="secondary"
                icon={faPen}
                iconOnly
                tooltip="Edit Goal"
                onClick={onConfigureClick}
                style={{
                  padding: "2px 4px",
                  borderRadius: "4px",
                  borderWidth: "1px",
                  minWidth: "auto",
                  fontSize: "0.6rem",
                }}
              />
            </div>
            {goalsUpdatedAt && (
              <span
                style={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "0.6rem",
                  fontWeight: 400,
                  color: "rgba(255, 255, 255, 0.45)",
                  marginTop: "4px",
                }}
              >
                Set on {formatLastSaved(goalsUpdatedAt)}
              </span>
            )}
          </div>
        </div>
      ) : (
        <div
          onClick={onConfigureClick}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            padding: "20px 28px",
            background: "rgba(102, 157, 46, 0.08)",
            border: "1px dashed rgba(102, 157, 46, 0.35)",
            borderRadius: "12px",
            cursor: "pointer",
          }}
        >
          <div
            style={{
              fontSize: "1.75rem",
              color: "#669d2e",
              marginBottom: "12px",
              opacity: 0.7,
            }}
          >
            <FontAwesomeIcon icon={faTrophy} />
          </div>
          <span
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: "6px",
            }}
          >
            Set your yearly goal
          </span>
          <span
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "0.7rem",
              fontWeight: 400,
              color: "var(--text-muted)",
              maxWidth: "180px",
              lineHeight: 1.5,
            }}
          >
            Track your progress towards your emissions reduction target
          </span>
        </div>
      )}
    </div>
  );
};

const CounterCard = ({
  icon,
  value,
  trend,
  title,
  desc,
  color,
  tooltip,
  darkMode,
  isLoading,
}) => {
  // Show trend arrow integrated into value (blue up, red down)
  const showTrendArrow =
    !isLoading &&
    trend !== undefined &&
    trend !== null &&
    typeof trend === "number";

  return (
    <div className={`ghg-counter-card ${color} ${darkMode ? "dark" : "light"}`}>
      <div className="ghg-counter-icon">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="ghg-counter-content">
        <div className="ghg-counter-value">
          {isLoading ? (
            <Skeleton width="100px" height="32px" borderRadius="6px" />
          ) : (
            <>
              {value}
              {showTrendArrow && (
                <span
                  className={`ghg-trend-arrow ${trend >= 0 ? "up" : "down"}`}
                >
                  {trend >= 0 ? "↑" : "↓"}
                </span>
              )}
            </>
          )}
        </div>
        <div className="ghg-counter-title">
          {title}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <span className="ghg-info-icon">
                <FontAwesomeIcon icon={faCircleInfo} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className="ghg-counter-desc">
          {isLoading ? (
            <Skeleton width="140px" height="14px" borderRadius="4px" />
          ) : (
            desc
          )}
        </div>
      </div>
    </div>
  );
};

const MaterialBarChart = ({ data, darkMode }) => {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <div className="ghg-empty-state">No material data available</div>;
  }

  const maxPct = Math.max(...data.map((d) => d.percentage));

  return (
    <div className={`ghg-chart-container ${darkMode ? "dark" : "light"}`}>
      <div className="ghg-bar-chart">
        {data.map((item) => (
          <div key={item.name} className="ghg-bar-row">
            <span className="ghg-bar-label">{item.name}</span>
            <div className="ghg-bar-track">
              <div
                className="ghg-bar-fill"
                style={{ width: `${(item.percentage / maxPct) * 100}%` }}
              >
                {item.percentage >= 5 && `${item.percentage}%`}
              </div>
            </div>
            <div className="ghg-bar-values">
              <span className="ghg-bar-value">
                {formatNum(Math.round(item.avoided))} kg CO₂e
              </span>
              {(item.energyKwh > 0 || item.wasteStageQty > 0) && (
                <span className="ghg-bar-sub-values">
                  {item.energyKwh > 0 && (
                    <span className="ghg-bar-sub" title={t("Energy Consumption")}>
                      <FontAwesomeIcon icon={faPlug} /> {formatNum(Math.round(item.energyKwh))} kWh
                    </span>
                  )}
                  {item.wasteStageQty > 0 && (
                    <span className="ghg-bar-sub" title={t("Waste Diverted")}>
                      <FontAwesomeIcon icon={faRecycle} /> {formatNum(Math.round(item.wasteStageQty))} kg
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MaterialTags = ({ materials }) => {
  if (!materials || materials.length === 0) return null;

  const visible = materials.slice(0, 3);
  const hidden = materials.slice(3);

  return (
    <div className="ghg-material-tags">
      {visible.map((m) => (
        <span key={m} className="ghg-material-tag">
          {m}
        </span>
      ))}
      {hidden.length > 0 && (
        <Tooltip
          title={
            <div className="ghg-materials-tooltip">
              <div className="ghg-materials-tooltip-header">Materials</div>
              <div className="ghg-materials-tooltip-list">
                {hidden.map((m) => (
                  <span key={m} className="ghg-materials-tooltip-item">
                    {m}
                  </span>
                ))}
              </div>
            </div>
          }
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "rgba(20, 30, 20, 0.95)",
                border: "1px solid rgba(102, 157, 46, 0.3)",
                borderRadius: "8px",
                padding: "0",
                maxWidth: "280px",
                "& .MuiTooltip-arrow": {
                  color: "rgba(20, 30, 20, 0.95)",
                },
              },
            },
          }}
        >
          <span className="ghg-material-more">+{hidden.length} more</span>
        </Tooltip>
      )}
    </div>
  );
};

const ReceiverTable = ({ data, darkMode }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const tableRef = useRef(null);

  const totals = useMemo(() => {
    if (!data || data.length === 0) return { qty: 0, avoided: 0, materials: 0 };
    return {
      qty: data.reduce((sum, r) => sum + (r.totalQty || 0), 0),
      avoided: data.reduce((sum, r) => sum + (r.avoided || 0), 0),
      materials: [...new Set(data.flatMap((r) => r.materials || []))].length,
    };
  }, [data]);

  // reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data]);

  // scroll to table top when page changes
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);

    // use scrollIntoView which works with any scroll container
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 50);
    }
  }, []);

  if (!data || data.length === 0) {
    return <div className="ghg-empty-state">No receiver data available</div>;
  }

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div
      className={`ghg-receiver-section ${darkMode ? "dark" : "light"}`}
      ref={tableRef}
      style={{ scrollMarginTop: "100px" }}
    >
      <div className="ghg-table-header">
        <div className="ghg-table-title">
          <FontAwesomeIcon icon={faBuilding} />
          <span>{data.length} recycling partners</span>
        </div>
      </div>
      <table className="ghg-receiver-table">
        <thead>
          <tr>
            <th>{t("Receiver")}</th>
            <th>{t("Materials")}</th>
            <th>{t("Total Qty (lbs)")}</th>
            <th>{t("Avoided (kg CO₂e)")}</th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((r) => (
            <tr key={r.id}>
              <td>
                <div className="ghg-receiver-info">
                  <div className="ghg-receiver-name">{r.name}</div>
                  <div className="ghg-receiver-location">{r.location}</div>
                </div>
              </td>
              <td>
                <MaterialTags materials={r.materials} />
              </td>
              <td className="ghg-qty-cell">
                {formatNum(Math.round(r.totalQty))}
                <span className="ghg-qty-percent">{r.qtyPercentage}%</span>
              </td>
              <td className="ghg-avoided-cell">
                <span className="ghg-avoided-value">
                  {formatNum(Math.round(r.avoided))}
                </span>
                <span className="ghg-avoided-percent">
                  {r.avoidedPercentage}%
                </span>
              </td>
            </tr>
          ))}
          {/* Show totals row on last page */}
          {currentPage === totalPages && (
            <tr className="ghg-totals-row">
              <td>
                <div className="ghg-receiver-info">
                  <div className="ghg-receiver-name">Total</div>
                </div>
              </td>
              <td>
                <span className="ghg-material-tag">
                  {totals.materials} materials
                </span>
              </td>
              <td className="ghg-qty-cell">
                {formatNum(Math.round(totals.qty))}
              </td>
              <td className="ghg-avoided-cell">
                <span className="ghg-avoided-value">
                  {formatNum(Math.round(totals.avoided))}
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ghg-pagination">
          <button
            className="ghg-pagination-btn"
            disabled={currentPage === 1}
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          >
            {t("Previous")}
          </button>
          <span className="ghg-pagination-info">
            {t("Page")} {currentPage} {t("of")} {totalPages}
          </span>
          <button
            className="ghg-pagination-btn"
            disabled={currentPage === totalPages}
            onClick={() =>
              handlePageChange(Math.min(totalPages, currentPage + 1))
            }
          >
            {t("Next")}
          </button>
        </div>
      )}
    </div>
  );
};

// Accordion component
const GHGAccordion = ({
  id,
  title,
  children,
  defaultExpanded = true,
  actions,
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    className="donor-inv-accordion"
    disableGutters
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      aria-controls={`${id}-content`}
      id={`${id}-header`}
      className="donor-inv-accordion-header"
    >
      <Typography className="donor-inv-accordion-title">{title}</Typography>
      {actions && (
        <div
          className="ghg-accordion-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      )}
    </AccordionSummary>
    <AccordionDetails className="donor-inv-accordion-content">
      {children}
    </AccordionDetails>
  </Accordion>
);

function DonorGHG() {
  const { darkMode } = useDarkMode();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // permission guard - redirects to /no-permission if user lacks access
  const { hasPermission, isChecking } = usePermissionGuard("donor", "ghg");

  const [isVisible, setIsVisible] = useState(false);
  const [appliedPreset, setAppliedPreset] = useState("allTime");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");
  const [pendingPreset, setPendingPreset] = useState("allTime");
  const [pendingDateFrom, setPendingDateFrom] = useState("");
  const [pendingDateTo, setPendingDateTo] = useState("");
  const [sankeyDrawerOpen, setSankeyDrawerOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const sankeyPanelRef = useRef(null);
  const heroRef = useRef(null);

  // toggle sankey with scroll behavior
  const handleSankeyToggle = useCallback(() => {
    const opening = !sankeyDrawerOpen;
    setSankeyDrawerOpen(opening);

    if (opening) {
      // scroll to sankey panel after it opens
      setTimeout(() => {
        if (sankeyPanelRef.current) {
          sankeyPanelRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 350); // wait for panel expand animation
    } else {
      // scroll to top on close
      setTimeout(() => {
        if (heroRef.current) {
          heroRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 50);
    }
  }, [sankeyDrawerOpen]);

  // navigate to profile GHG goals section
  const handleConfigureGoal = useCallback(() => {
    navigate("/profile?tab=ghg-goals");
  }, [navigate]);

  // check if there are pending changes
  const hasPendingChanges = useMemo(() => {
    return (
      pendingPreset !== appliedPreset ||
      pendingDateFrom !== appliedDateFrom ||
      pendingDateTo !== appliedDateTo
    );
  }, [
    pendingPreset,
    pendingDateFrom,
    pendingDateTo,
    appliedPreset,
    appliedDateFrom,
    appliedDateTo,
  ]);

  const filters = useMemo(
    () => ({
      dateFrom: appliedDateFrom || null,
      dateTo: appliedDateTo || null,
    }),
    [appliedDateFrom, appliedDateTo],
  );

  const {
    loading,
    refreshing,
    error,
    data,
    refresh,
    lastUpdated,
    getYearlyGoal,
    goalsUpdatedAt,
  } = useDonorGHGData(filters);

  const lastApplyTime = useRef(0);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // handle preset click - just update pending state, don't apply
  const handlePresetClick = useCallback((key) => {
    setPendingPreset(key);
    const range = getPresetRange(key);
    setPendingDateFrom(range.start ? toInputDate(range.start) : "");
    setPendingDateTo(range.end ? toInputDate(range.end) : "");
  }, []);

  // handle custom date change
  const handleDateChange = useCallback((field, value) => {
    setPendingPreset("custom");
    if (field === "from") setPendingDateFrom(value);
    else setPendingDateTo(value);
  }, []);

  // apply filters with debounce protection
  const handleApplyFilters = useCallback(() => {
    const now = Date.now();
    if (now - lastApplyTime.current < 1000) return; // 1 second debounce
    lastApplyTime.current = now;

    setAppliedPreset(pendingPreset);
    setAppliedDateFrom(pendingDateFrom);
    setAppliedDateTo(pendingDateTo);
    refresh({
      dateFrom: pendingDateFrom || null,
      dateTo: pendingDateTo || null,
    });
  }, [pendingPreset, pendingDateFrom, pendingDateTo, refresh]);

  const handleReset = useCallback(() => {
    setPendingPreset("allTime");
    setPendingDateFrom("");
    setPendingDateTo("");
    setAppliedPreset("allTime");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    refresh({ dateFrom: null, dateTo: null });
  }, [refresh]);

  // export CSV
  const handleExport = useCallback(() => {
    if (!data?.receiverBreakdown?.length) return;

    const headers = [
      "Receiver",
      "Location",
      "Materials",
      "Total Qty (lbs)",
      "Qty %",
      "Avoided (kg CO2e)",
      "Avoided %",
    ];
    const rows = data.receiverBreakdown.map((r) => [
      r.name,
      r.location,
      (r.materials || []).join("; "),
      r.totalQty,
      r.qtyPercentage,
      r.avoided,
      r.avoidedPercentage,
    ]);

    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ghg-impact-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  // show loading while checking permissions
  if (isChecking) {
    return (
      <LoadingSpinner fullPage size={50} message={t("Checking permissions...")} />
    );
  }

  // if no permission, the hook will redirect - but just in case, don't render
  if (!hasPermission) {
    return null;
  }

  if (loading) {
    return (
      <LoadingSpinner fullPage size={50} message={t("Loading GHG data...")} />
    );
  }

  const { rolling30, avgPerDelivery, totalDeliveries, topMaterial, totalEnergyKwh, totalWasteStageQty } =
    data || {};
  const trendData = formatTrendValue(rolling30);
  const periodLabel = getPeriodLabel(
    appliedPreset,
    appliedDateFrom,
    appliedDateTo,
  );

  // calculate goal based on selected year (or current year for "all time")
  const getSelectedYear = () => {
    if (appliedDateTo) {
      return new Date(appliedDateTo).getFullYear();
    }
    if (appliedDateFrom) {
      return new Date(appliedDateFrom).getFullYear();
    }
    return new Date().getFullYear();
  };
  const selectedYear = getSelectedYear();
  const goalTarget = getYearlyGoal(selectedYear);
  const goalPct = goalTarget
    ? Math.min(100, Math.round(((data?.totalAvoided || 0) / goalTarget) * 100))
    : 0;

  // the 30-day comparison is always rolling (independent of period filter)
  const trendDesc = trendData.isFirstPeriod
    ? `${trendData.deliveryCount || rolling30?.deliveryCount || 0} deliveries in last 30 days`
    : `vs prior 30 days (${trendData.deliveryCount || rolling30?.deliveryCount || 0} deliveries)`;

  const trendTitle = trendData.isFirstPeriod
    ? t("Last 30 Days")
    : t("vs Previous 30 Days");

  const trendTooltip = trendData.isFirstPeriod
    ? t(
        "Total emissions avoided in the last 30 days. Not enough prior data for comparison.",
      )
    : t(
        "Compares the last 30 days to the prior 30 days. This metric is always rolling, independent of the period filter.",
      );

  // check if filters differ from default
  const isModified =
    appliedPreset !== "allTime" ||
    appliedDateFrom !== "" ||
    appliedDateTo !== "";

  return (
    <div
      className={`donor-inventory-container ${darkMode ? "dark" : "light"} ${isVisible ? "visible" : ""}`}
    >
      {/* Page header */}
      <div className="page-header-row">
        <div />
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ActionButton
            variant="secondary"
            icon={faFileExport}
            onClick={() => setIsReportModalOpen(true)}
          >
            {t("Create Report")}
          </ActionButton>
          <LastUpdatedBadge
            timestamp={lastUpdated}
            onRefresh={() => refresh(filters)}
            isRefreshing={refreshing}
          />
        </div>
      </div>

      {error && (
        <div className="donor-inv-content-wrapper" style={{ marginBottom: 24 }}>
          <Alert message={error} severity="error" />
        </div>
      )}

      {/* Impact hero banner */}
      <div
        ref={heroRef}
        className={`donor-inv-content-wrapper ghg-hero-wrapper ${refreshing ? "is-refreshing" : ""}`}
      >
        <ImpactHero
          total={data?.totalAvoided || 0}
          goalTarget={goalTarget}
          goalPct={goalPct}
          periodLabel={periodLabel}
          darkMode={darkMode}
          isLoading={refreshing}
          goalsUpdatedAt={goalsUpdatedAt}
          onConfigureClick={handleConfigureGoal}
          onVisualizeClick={handleSankeyToggle}
          isVisualizingFlow={sankeyDrawerOpen}
        />
      </div>

      {/* Sankey Panel (inline below hero) */}
      <div ref={sankeyPanelRef} className={`ghg-sankey-panel ${sankeyDrawerOpen ? "open" : ""}`}>
        <div className="donor-inv-content-wrapper">
          <div className="ghg-sankey-panel-card">
            {sankeyDrawerOpen && (
              <SankeyPanel
                initialReceiverBreakdown={data?.receiverBreakdown}
                initialMaterialBreakdown={data?.materialBreakdown}
                initialTotalQty={(data?.receiverBreakdown || []).reduce((sum, r) => sum + (r.totalQty || 0), 0)}
                initialTotalAvoided={data?.totalAvoided || 0}
                darkMode={darkMode}
                pagePreset={appliedPreset}
                scrollTargetRef={sankeyPanelRef}
              />
            )}
          </div>
        </div>
      </div>

      {/* Counter cards */}
      <div
        className={`donor-inv-counters-wrapper ${refreshing ? "is-refreshing" : ""}`}
      >
        <div className="ghg-counters-grid">
          <CounterCard
            icon={faArrowTrendUp}
            value={trendData.value}
            trend={trendData.hasBaseline ? trendData.trend : null}
            title={trendTitle}
            desc={trendDesc}
            color="blue"
            tooltip={trendTooltip}
            darkMode={darkMode}
            isLoading={refreshing}
          />
          <CounterCard
            icon={faBolt}
            value={`${Math.round(avgPerDelivery || 0)} kg`}
            title={t("Avg per Delivery")}
            desc={`${totalDeliveries || 0} deliveries in selected period`}
            color="amber"
            darkMode={darkMode}
            isLoading={refreshing}
          />
          <CounterCard
            icon={faTrophy}
            value={topMaterial?.name || "-"}
            title={t("Top Material")}
            desc={`${topMaterial?.percentage || 0}% of total (${formatNum(Math.round(topMaterial?.avoided || 0))} kg CO₂e)`}
            color="purple"
            darkMode={darkMode}
            isLoading={refreshing}
          />
          <CounterCard
            icon={faPlug}
            value={`${formatNum(Math.round(totalEnergyKwh || 0))} kWh`}
            title={t("Energy Consumption")}
            desc={t("Total energy from delivered materials")}
            color="green"
            darkMode={darkMode}
            isLoading={refreshing}
          />
          <CounterCard
            icon={faRecycle}
            value={`${formatNum(Math.round(totalWasteStageQty || 0))} kg`}
            title={t("Waste Diverted")}
            desc={t("Material diverted from waste stage")}
            color="teal"
            darkMode={darkMode}
            isLoading={refreshing}
          />
        </div>
      </div>

      {/* Filters bar */}
      <div className="donor-inv-filters-wrapper">
        <div className="donor-inv-filters ghg-filters">
          {/* Presets */}
          <Box className="donor-filter-group">
            <Typography variant="caption" className="donor-filter-label">
              {t("Period")}
            </Typography>
            <div className="ghg-presets-row">
              {[
                { key: "thisMonth", label: t("This Month") },
                { key: "lastMonth", label: t("Last Month") },
                { key: "thisQuarter", label: t("This Quarter") },
                { key: "thisYear", label: t("This Year") },
                { key: "lastYear", label: t("Last Year") },
                { key: "allTime", label: t("All Time") },
              ].map((p) => (
                <button
                  key={p.key}
                  className={`ghg-preset-btn ${pendingPreset === p.key ? "active" : ""}`}
                  onClick={() => handlePresetClick(p.key)}
                  disabled={refreshing}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* Custom date range + actions */}
          <Box className="donor-filter-group ghg-custom-range-group">
            <Typography variant="caption" className="donor-filter-label">
              {t("Custom")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                type="date"
                size="small"
                value={pendingDateFrom}
                onChange={(e) => handleDateChange("from", e.target.value)}
                className="donor-filter-input ghg-date-input"
                inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
                disabled={refreshing}
              />
              <span className="ghg-date-separator">{t("to")}</span>
              <TextField
                type="date"
                size="small"
                value={pendingDateTo}
                onChange={(e) => handleDateChange("to", e.target.value)}
                className="donor-filter-input ghg-date-input"
                inputProps={{ style: { color: darkMode ? "#fff" : "#1a1a1a" } }}
                disabled={refreshing}
              />
            </Stack>
          </Box>

          {/* Filter actions with divider */}
          <div className="ghg-filter-actions">
            {isModified && (
              <ActionButton
                variant="default"
                icon={faUndo}
                onClick={handleReset}
                disabled={refreshing}
              >
                {t("Reset")}
              </ActionButton>
            )}

            <ActionButton
              variant="secondary"
              icon={faFilter}
              onClick={handleApplyFilters}
              disabled={!hasPendingChanges || refreshing}
              className={refreshing ? "is-loading" : ""}
            >
              {refreshing ? t("Loading...") : t("Apply")}
            </ActionButton>
          </div>
        </div>

        {/* Showing indicator */}
        <div className="ghg-showing-indicator">
          <span className="ghg-showing-label">{t("Showing")}:</span>
          <span className="ghg-showing-value">
            {appliedDateFrom && appliedDateTo
              ? `${formatDateDisplay(appliedDateFrom)} – ${formatDateDisplay(appliedDateTo)}`
              : appliedDateFrom
                ? `From ${formatDateDisplay(appliedDateFrom)}`
                : appliedDateTo
                  ? `Until ${formatDateDisplay(appliedDateTo)}`
                  : t("All available data")}
            {appliedPreset &&
              appliedPreset !== "allTime" &&
              appliedPreset !== "custom" && (
                <span className="ghg-showing-preset">
                  ({getPresetRange(appliedPreset).label})
                </span>
              )}
          </span>
        </div>
      </div>

      {/* Accordions */}
      <div
        className={`donor-inv-content-wrapper ${refreshing ? "is-refreshing" : ""}`}
      >
        <GHGAccordion
          id="emissions-by-material"
          title={t("Emissions by Material")}
        >
          <MaterialBarChart
            data={data?.materialBreakdown}
            darkMode={darkMode}
          />
        </GHGAccordion>

        <GHGAccordion
          id="impact-by-receiver"
          title={t("Impact by Receiver")}
          actions={
            <ActionButton
              variant="secondary"
              icon={faDownload}
              onClick={handleExport}
              disabled={!data?.receiverBreakdown?.length}
            >
              {t("Export")}
            </ActionButton>
          }
        >
          <ReceiverTable data={data?.receiverBreakdown} darkMode={darkMode} />
        </GHGAccordion>
      </div>

      <CreateReportModal
        open={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        darkMode={darkMode}
        initialDateFrom={appliedDateFrom}
        initialDateTo={appliedDateTo}
        receivers={data?.receiverBreakdown || []}
        companyName={user?.company_name || ""}
      />
    </div>
  );
}

export default DonorGHG;
